import * as React from 'react'
import { AlertTriangle, FileSpreadsheet, Rows3 } from 'lucide-react'
import { cn } from '../../lib/utils'
import {
  getSpreadsheetColumnName,
  getSpreadsheetColumnWidths,
  getSpreadsheetRowWindow,
  parseSpreadsheetPreview,
  type SpreadsheetPreviewDocument,
  type SpreadsheetPreviewSheet,
} from '../../lib/spreadsheet-preview'
import { FullscreenOverlayBase } from './FullscreenOverlayBase'
import { FullscreenOverlayBaseHeader } from './FullscreenOverlayBaseHeader'
import { PreviewOverlay } from './PreviewOverlay'
import { useTranslation } from '@okclawpro/i18n/react'

export interface SpreadsheetPreviewOverlayProps {
  content: string
  isOpen: boolean
  onClose: () => void
  filePath: string
  theme?: 'light' | 'dark'
  error?: string
  embedded?: boolean
}

function formatGridCount(value: number): string {
  return new Intl.NumberFormat().format(value)
}

function SpreadsheetGrid({ sheet }: { sheet: SpreadsheetPreviewSheet }) {
  const ROW_HEIGHT = 28
  const COLUMN_HEADER_HEIGHT = 28
  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  const scrollRafRef = React.useRef<number | null>(null)
  const pendingScrollTopRef = React.useRef(0)
  const [scrollTop, setScrollTop] = React.useState(0)
  const [viewportHeight, setViewportHeight] = React.useState(560)
  const widths = React.useMemo(() => getSpreadsheetColumnWidths(sheet.rows), [sheet])
  const columnCount = widths.length
  const rowWindow = React.useMemo(() => getSpreadsheetRowWindow({
    rowCount: sheet.rows.length,
    scrollTop,
    viewportHeight,
    rowHeight: ROW_HEIGHT,
    columnHeaderHeight: COLUMN_HEADER_HEIGHT,
  }), [scrollTop, sheet.rows.length, viewportHeight])
  const visibleRowIndices = React.useMemo(() => {
    const indices = sheet.rows.length > 0 ? [0] : []
    for (let index = rowWindow.start; index <= rowWindow.end; index++) indices.push(index)
    return indices
  }, [rowWindow.end, rowWindow.start, sheet.rows.length])

  React.useEffect(() => {
    const root = scrollRef.current
    if (!root || typeof ResizeObserver === 'undefined') return
    const update = () => setViewportHeight(previous => previous === root.clientHeight ? previous : root.clientHeight)
    update()
    const observer = new ResizeObserver(update)
    observer.observe(root)
    return () => observer.disconnect()
  }, [sheet])

  React.useEffect(() => () => {
    if (scrollRafRef.current != null) cancelAnimationFrame(scrollRafRef.current)
  }, [])

  const handleScroll = React.useCallback((event: React.UIEvent<HTMLDivElement>) => {
    pendingScrollTopRef.current = event.currentTarget.scrollTop
    if (scrollRafRef.current != null) return
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null
      setScrollTop(pendingScrollTopRef.current)
    })
  }, [])

  const renderRow = (rowIndex: number) => {
    const row = sheet.rows[rowIndex] ?? []
    return (
      <tr key={rowIndex} className="group/row" aria-rowindex={rowIndex + 2}>
        <th
          className={cn(
            'sticky left-0 z-20 h-7 border-b border-r border-foreground/[0.075] px-1.5 text-center text-[10.5px] font-normal tabular-nums text-muted-foreground/58',
            rowIndex === 0 ? 'top-7 bg-foreground-3 font-medium' : 'bg-foreground-3 group-hover/row:bg-foreground/[0.055]',
          )}
          scope="row"
        >
          {rowIndex + 1}
        </th>
        {Array.from({ length: columnCount }, (_, columnIndex) => {
          const value = row[columnIndex] ?? ''
          return (
            <td
              key={columnIndex}
              tabIndex={-1}
              className={cn(
                'h-7 cursor-cell overflow-hidden whitespace-nowrap border-b border-r border-foreground/[0.075] px-2 align-middle tabular-nums transition-[background-color,box-shadow] duration-150 group-hover/row:bg-foreground/[0.025]',
                'focus:outline-none focus-visible:relative focus-visible:z-10 focus-visible:bg-primary/[0.035] focus-visible:shadow-[inset_0_0_0_1.5px_color-mix(in_srgb,var(--primary)_68%,transparent)]',
                rowIndex === 0 && 'sticky top-7 z-10 bg-foreground-3 font-semibold text-foreground group-hover/row:bg-foreground-3',
              )}
              title={value || undefined}
            >
              <div className="overflow-hidden text-ellipsis whitespace-nowrap">{value}</div>
            </td>
          )
        })}
      </tr>
    )
  }

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="spreadsheet-grid-scrollbar relative min-h-0 flex-1 overflow-auto overscroll-contain bg-background"
    >
      <table
        className="w-max min-w-full table-fixed border-separate border-spacing-0 text-[11.5px] leading-5 text-foreground/82"
        aria-label={`${sheet.name} 工作表，只读预览`}
        aria-rowcount={sheet.rows.length + 1}
      >
        <colgroup>
          <col style={{ width: 42 }} />
          {widths.map((width, index) => <col key={index} style={{ width }} />)}
        </colgroup>
        <thead>
          <tr>
            <th
              className="sticky left-0 top-0 z-40 h-7 border-b border-r border-foreground/10 bg-foreground-3 shadow-[1px_1px_0_color-mix(in_srgb,currentColor_5%,transparent)]"
              aria-label="Row numbers"
            />
            {Array.from({ length: columnCount }, (_, columnIndex) => (
              <th
                key={columnIndex}
                className="sticky top-0 z-30 h-7 border-b border-r border-foreground/10 bg-foreground-3 px-2 text-center text-[10.5px] font-medium tracking-[0.02em] text-muted-foreground/68"
                scope="col"
              >
                {getSpreadsheetColumnName(columnIndex)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRowIndices.map((rowIndex, position) => (
            <React.Fragment key={rowIndex}>
              {position === 1 && rowWindow.topSpacerHeight > 0 && (
                <tr aria-hidden="true">
                  <td colSpan={columnCount + 1} className="border-0 p-0" style={{ height: rowWindow.topSpacerHeight }} />
                </tr>
              )}
              {renderRow(rowIndex)}
            </React.Fragment>
          ))}
          {rowWindow.bottomSpacerHeight > 0 && (
            <tr aria-hidden="true">
              <td colSpan={columnCount + 1} className="border-0 p-0" style={{ height: rowWindow.bottomSpacerHeight }} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

function SpreadsheetCanvas({ document }: { document: SpreadsheetPreviewDocument }) {
  const [activeSheetIndex, setActiveSheetIndex] = React.useState(0)
  const activeSheet = document.sheets[Math.min(activeSheetIndex, document.sheets.length - 1)]!
  const shownRows = activeSheet.rows.length
  const shownColumns = activeSheet.rows.reduce((maximum, row) => Math.max(maximum, row.length), 0)
  const isTruncated = document.truncated || activeSheet.truncated

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <div className="flex h-9 shrink-0 items-center justify-between gap-3 border-b border-foreground/[0.075] bg-background px-3">
        <div className="flex min-w-0 items-center gap-2">
          <Rows3 className="h-3.5 w-3.5 shrink-0 text-success/75" />
          <span className="truncate text-[11.5px] font-semibold text-foreground/82">{activeSheet.name}</span>
          <span className="shrink-0 text-[10.5px] tabular-nums text-muted-foreground/55">
            {formatGridCount(shownRows)} 行 × {formatGridCount(shownColumns)} 列
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2 text-[10.5px] text-muted-foreground/55">
          {isTruncated && (
            <span className="flex items-center gap-1" title={`原文件约 ${activeSheet.totalRows} 行 × ${activeSheet.totalColumns} 列`}>
              <AlertTriangle className="h-3 w-3" />
              已显示预览范围
            </span>
          )}
          <span className="rounded-[4px] border border-foreground/[0.07] bg-foreground/[0.025] px-1.5 py-px">只读</span>
        </div>
      </div>

      <SpreadsheetGrid key={activeSheet.name} sheet={activeSheet} />

      <div className="flex h-8 shrink-0 items-stretch border-t border-foreground/[0.09] bg-foreground-3 px-1.5">
        <div className="flex min-w-0 flex-1 items-stretch overflow-x-auto spreadsheet-tabs-scrollbar">
          {document.sheets.map((sheet, index) => (
            <button
              key={`${sheet.name}-${index}`}
              type="button"
              onClick={() => setActiveSheetIndex(index)}
              className={cn(
                'relative min-w-[84px] max-w-[190px] shrink-0 border-r border-foreground/[0.07] px-3 text-left text-[10.5px] transition-colors duration-150 focus:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring',
                index === activeSheetIndex
                  ? 'bg-background font-semibold text-foreground after:absolute after:inset-x-2 after:top-0 after:h-0.5 after:rounded-full after:bg-success/80'
                  : 'text-muted-foreground hover:bg-foreground/[0.035] hover:text-foreground/80',
              )}
              title={sheet.name}
            >
              <span className="block truncate">{sheet.name}</span>
            </button>
          ))}
        </div>
        <div className="flex shrink-0 items-center pl-3 text-[9.5px] tabular-nums text-muted-foreground/45">
          {document.sheets.length} 个工作表
        </div>
      </div>
    </div>
  )
}

export function SpreadsheetPreviewOverlay({
  content,
  isOpen,
  onClose,
  filePath,
  theme = 'light',
  error,
  embedded = false,
}: SpreadsheetPreviewOverlayProps) {
  const { t } = useTranslation()
  const document = React.useMemo(() => parseSpreadsheetPreview(content), [content])
  const typeBadge = { icon: FileSpreadsheet, label: t('overlay.spreadsheet'), variant: 'green' as const }

  if (!document) {
    return (
      <PreviewOverlay
        isOpen={isOpen}
        onClose={onClose}
        theme={theme}
        typeBadge={typeBadge}
        filePath={filePath}
        error={{ label: t('overlay.previewFailed'), message: error || t('overlay.previewDataUnavailable') }}
        embedded={embedded}
        className="bg-foreground-3"
      >
        <div className="flex min-h-[180px] items-center justify-center px-6 text-sm text-muted-foreground">
          {t('overlay.openWithSystemApp')}
        </div>
      </PreviewOverlay>
    )
  }

  if (embedded) {
    if (!isOpen) return null
    return (
      <div className="flex h-full w-full flex-col overflow-hidden rounded-[10px] border border-foreground/[0.07] bg-background shadow-minimal">
        <FullscreenOverlayBaseHeader
          onClose={onClose}
          filePath={filePath}
          typeBadge={typeBadge}
          subtitle={t('overlay.sheetCount', { count: document.sheets.length })}
        />
        {error && (
          <div className="border-y border-destructive/15 bg-destructive/5 px-4 py-2 text-xs text-destructive">
            {error}
          </div>
        )}
        <SpreadsheetCanvas document={document} />
      </div>
    )
  }

  return (
    <FullscreenOverlayBase
      isOpen={isOpen}
      onClose={onClose}
      filePath={filePath}
      typeBadge={typeBadge}
      subtitle={t('overlay.sheetCount', { count: document.sheets.length })}
      className="bg-foreground-3"
      contentMode="flow"
    >
      <div className="mx-3 flex h-[calc(100vh-88px)] min-h-[420px] overflow-hidden rounded-[10px] border border-foreground/[0.08] bg-background shadow-strong">
        <SpreadsheetCanvas document={document} />
      </div>
    </FullscreenOverlayBase>
  )
}
